import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { logOperationalWarning } from '../utils/safeLogger';
import { API_ORIGIN, TIME_PER_QUESTION_MS } from '../utils/homeHelpers';
import {
  getDailyTriviaQuestions,
  _getPlayedToday,
  _setPlayedToday,
  _clearPlayedToday,
} from '../utils/dailyTrivia';
import {
  _todayKey,
  _computeStreak,
  _bestScore,
  _loadTriviaHistory,
  _saveTriviaHistory,
} from '../utils/triviaHelpers';
import {
  _computeGamificationFromStats,
  _normalizeGamiForUI,
} from '../utils/gamificationHelpers';
import {
  _saveGamiCache,
  _saveUserStatsCache,
} from '../utils/userStatsCache';
import { authenticatedRequest, requestServerLogout, logoutLocal, broadcastAuthLogout } from '../services/authApi';
import { isTriviaAnswerCorrect } from '../utils/triviaContent';
import { getAuthToken } from '../utils/auth';


const TRIVIA_CACHE_KEY = 'zodiacus_trivia_cache_v1';

const noopSetter = () => {};

const safeSetter = (setter, setterName) => {
  if (typeof setter === 'function') return setter;

  return (...args) => {
    console.warn(`[trivia] ${setterName} is not a function`, {
      received: setter,
      args,
    });
  };
};

const useTriviaGame = ({
  resolvedUid,
  token,
  syncServerTime,
  zodiacSign,
  countryCode,
  triviaStartedAt,
  clockOffsetMs,
  lbScope,
  lbPeriod,
  userStats,
  showTrivia,
  questions,
  qIndex,
  total,
  score,
  answered,
  current,
  timeLeft,
  ticking,
  questionStartedAt,
  triviaMode,
  playedToday,
  setTriviaStartedAt,
  fetchLeaderboard,
  fetchUserStats,
  fetchGamification,
  setLbScope,
  setAnswered,
  setPickedIdx,
  setScore,
  setTicking,
  setTimeLeft,
  setQIndex,
  setQuestionStartedAt,
  setFinished,
  setShowTrivia,
  setHistory,
  setStreak,
  setBest,
  setPlayedToday,
  setTriviaMode,
  setUserStats,
  setGami,
  markTriviaAsPlayedMode,
  resetTriviaBaseState,
  startTriviaPlayState,
  sessionStartedRef,
  triviaOpenInflightRef,
  setQuestions,
  setLoadingTrivia,
  spoiledRef,
  triviaAdvanceTimeoutRef,
  persistTriviaResultRef,
}) => {
  const { i18n } = useTranslation('home');
  const finalizationInFlightRef = useRef(false);
  const safeSetShowTrivia = typeof setShowTrivia === 'function'
    ? setShowTrivia
    : safeSetter(setShowTrivia, 'setShowTrivia');

  const safeSetLoadingTrivia = typeof setLoadingTrivia === 'function'
    ? setLoadingTrivia
    : noopSetter;

  const checkAlreadyPlayedToday = useCallback(async () => {
    // 1) Server participation authority is independent of ranking. A valid
    // zero-point day has no rank but must still remain replay-blocking.
    try {
      if (!resolvedUid) return false;

      const params = new URLSearchParams({
        scope: 'global',
        limit: '1',
        userId: String(resolvedUid),
        period: 'today',
      });

      const url = `${API_ORIGIN}/api/trivia/leaderboard?${params.toString()}`;
      const res = await fetch(url, { credentials: 'include' });
      const j = await res.json().catch(() => ({}));

      if (j && j.ok && (j.playedToday === true || typeof j.myScore === 'number')) {
        return true;
      }
    } catch {}

    return _getPlayedToday(resolvedUid);
  }, [resolvedUid]);

  const buildTriviaSubmitBody = useCallback(({ safeScore, safeTotal, zodiacSign, country, startedAt, finishedAt }) => ({
    userId: resolvedUid,
    score: safeScore,
    total: safeTotal,
    sign: (zodiacSign || '').toString().toLowerCase(),
    country: (country || '').toString().toUpperCase(),
    startedAt: startedAt || null,
    finishedAt,
  }), [resolvedUid]);

  const normalizeTriviaSubmitInput = useCallback((sc, tot) => ({
    safeScore: Math.max(0, Number(sc || 0) || 0),
    safeTotal: Math.max(0, Number(tot || 0) || 0),
  }), []);

  const getTriviaAuthToken = useCallback((token) => token || getAuthToken() || '', []);

  const submitTriviaResultToApi = useCallback(async ({ body, authToken }) => {
    const onLocalhost = typeof window !== 'undefined' && /localhost/i.test(window.location.hostname);
    const headers = {
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    };
    if (onLocalhost) headers['X-Trivia-Dev'] = '1';

    const res = await authenticatedRequest(`${API_ORIGIN}/api/trivia/submit`, {
      rawResponse: true,
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      credentials: 'include',
      diagSource: 'useTriviaGame.submit',
    });

    let json = null;
    try { json = await res.json(); } catch { json = null; }

    return {
      ok: !!res.ok,
      status: res.status,
      json,
      alreadyPlayed: !!(json && json.alreadyPlayed),
      error: json && json.error ? json.error : null,
    };
  }, []);

  const buildOptimisticTriviaResult = useCallback(({ prevStats, prevHistory, dayKey, safeScore, safeTotal }) => {
    const baseHistory = Array.isArray(prevHistory) ? prevHistory : [];
    const filtered = baseHistory.filter((r) => r.day !== dayKey);
    const nextHistory = [
      ...filtered,
      { day: dayKey, score: safeScore, total: safeTotal, ts: Date.now() },
    ];

    const nextStreak = _computeStreak(nextHistory);
    const nextBest = _bestScore(nextHistory);

    const prevGames = Number(prevStats?.totalGames || 0);
    const prevTotal = Number(prevStats?.totalScore || 0);
    const prevBest = Number(prevStats?.best || 0);
    const hadToday = String(prevStats?.lastPlayed || '') === String(dayKey);

    const nextGames = hadToday ? prevGames : (prevGames + 1);
    const nextTotal = prevTotal + safeScore;
    const nextAvg = nextGames > 0 ? (nextTotal / nextGames) : 0;

    const optimisticStats = {
      ...(prevStats || {}),
      totalGames: nextGames,
      totalScore: nextTotal,
      avgScore: nextAvg,
      lastPlayed: String(dayKey),
      streak: Math.max(Number(prevStats?.streak || 0), Number(nextStreak || 0)),
      best: Math.max(prevBest, safeScore),
    };

    const optimisticGami = _normalizeGamiForUI(
      _computeGamificationFromStats(optimisticStats),
      optimisticStats
    );

    return {
      nextHistory,
      nextStreak,
      nextBest,
      optimisticStats,
      optimisticGami,
    };
  }, []);

  const persistTriviaResult = useCallback(async (dayKey, sc, tot) => {
    let resultWasSaved = false;
    try {
      const { safeScore, safeTotal } = normalizeTriviaSubmitInput(sc, tot);
      const authToken = getTriviaAuthToken(token);

      const body = buildTriviaSubmitBody({
        safeScore,
        safeTotal,
        zodiacSign,
        country: countryCode || localStorage.getItem('country') || '',
        startedAt: triviaStartedAt,
        finishedAt: Date.now() + clockOffsetMs,
      });

      if (!body.userId) {
        console.warn('[trivia] skip submit: missing userId (not logged in)');
        fetchLeaderboard(lbScope);
        return false;
      }

      const submitResult = await submitTriviaResultToApi({ body, authToken });

      if (!submitResult.ok) {
        if (submitResult.error === 'too_fast') {
          logOperationalWarning('trivia.submit', { status: submitResult.status, code: 'too_fast' });
        } else if (submitResult.error) {
          console.warn('submit failed', submitResult.error);
        } else {
          console.warn('submit failed', submitResult.status);
        }
        fetchLeaderboard(lbScope);
        return false;
      }

      if (submitResult.alreadyPlayed) {
        markTriviaAsPlayedMode();
        fetchLeaderboard(lbScope);
        return true;
      }

      const prev = _loadTriviaHistory(resolvedUid);
      const optimisticResult = buildOptimisticTriviaResult({
        prevStats: userStats,
        prevHistory: prev,
        dayKey,
        safeScore,
        safeTotal,
      });

      _saveTriviaHistory(resolvedUid, optimisticResult.nextHistory);
      setHistory(optimisticResult.nextHistory);
      setStreak(optimisticResult.nextStreak);
      setBest(optimisticResult.nextBest);
      _setPlayedToday(resolvedUid, dayKey);
      setPlayedToday(true);
      setTriviaMode('played');
      resultWasSaved = true;

      try {
        setUserStats(optimisticResult.optimisticStats);
        setGami(optimisticResult.optimisticGami);
        _saveGamiCache(resolvedUid, optimisticResult.optimisticGami);
        _saveUserStatsCache(resolvedUid, optimisticResult.optimisticStats);
      } catch {}

      setTicking(false);

      await fetchUserStats();
      await fetchGamification();
      // One completed result changes both approved leaderboard views. Refresh
      // each once; this is a post-submit sync, never polling.
      fetchLeaderboard(lbScope, null, { preferCacheFirst: false, period: 'month', viewPeriod: lbPeriod });
      fetchLeaderboard(lbScope, null, { preferCacheFirst: false, period: 'today', viewPeriod: lbPeriod });
      return true;
    } catch (e) {
      console.warn('submit error', e);
      fetchLeaderboard(lbScope);
      return resultWasSaved;
    }
  }, [
    resolvedUid,
    token,
    zodiacSign,
    countryCode,
    triviaStartedAt,
    clockOffsetMs,
    lbScope,
    lbPeriod,
    userStats,
    normalizeTriviaSubmitInput,
    getTriviaAuthToken,
    buildTriviaSubmitBody,
    submitTriviaResultToApi,
    buildOptimisticTriviaResult,
    fetchLeaderboard,
    fetchUserStats,
    fetchGamification,
    markTriviaAsPlayedMode,
    setHistory,
    setStreak,
    setBest,
    setPlayedToday,
    setTriviaMode,
    setUserStats,
    setGami,
    setTicking,
  ]);

  const resetTriviaAndClose = useCallback(async () => {
    try {
      if (triviaMode === 'play' && sessionStartedRef.current) {
        const dayKey = _todayKey();
        const partialScore = Number(score || 0);
        const tot = Number(total || 0);

        try {
          await persistTriviaResult(dayKey, partialScore, tot);
          _setPlayedToday(resolvedUid, dayKey);
          setPlayedToday(true);
        } catch {}
      }
    } catch {}

    try {
      if (triviaAdvanceTimeoutRef.current) clearTimeout(triviaAdvanceTimeoutRef.current);
    } catch {}

    triviaAdvanceTimeoutRef.current = null;

    safeSetShowTrivia(false);
    resetTriviaBaseState();
  }, [
    persistTriviaResult,
    resolvedUid,
    score,
    total,
    triviaMode,
    sessionStartedRef,
    setPlayedToday,
    safeSetShowTrivia,
    resetTriviaBaseState,
    triviaAdvanceTimeoutRef,
  ]);

const openTriviaModal = useCallback(async (mode = 'play') => {
  const requestedMode = mode || 'play';
    if (triviaOpenInflightRef.current) return;
    triviaOpenInflightRef.current = true;

    try {
      // Clean stale question state before the modal becomes visible.
      // Important on iOS/Safari: otherwise it may paint the previous question for one frame.
    resetTriviaBaseState();
    setQuestions([]);
    setAnswered(false);
    setPickedIdx(-1);
    setQIndex(0);
    setTicking(false);
    setTimeLeft(TIME_PER_QUESTION_MS);
    setQuestionStartedAt(null);
    setFinished(false);
    setTriviaMode(requestedMode === 'play' ? 'play' : 'played');
    safeSetLoadingTrivia(true);
    safeSetShowTrivia(true);

      const initialScope = 'global';
      setLbScope(initialScope);

      try {
        const serverNow = await syncServerTime();
        setTriviaStartedAt((prev) => prev || (serverNow ?? (Date.now() + clockOffsetMs)));
      } catch {}

      if (process.env.NODE_ENV === 'production') {
        fetchUserStats(true).catch(() => {});
      }

    if (requestedMode !== 'play') {
      fetchLeaderboard(initialScope, null, { preferCacheFirst: true, viewScope: initialScope }).catch(() => {});
    }

    const already = await checkAlreadyPlayedToday();

if (already) {
  resetTriviaBaseState();
  setQuestions([]);
  markTriviaAsPlayedMode();
  setPlayedToday(true);
  setFinished(true);
  setTriviaMode('played');
  sessionStartedRef.current = false;
  spoiledRef.current = false;
  safeSetLoadingTrivia(false);
  fetchLeaderboard(initialScope, null, { preferCacheFirst: true, viewScope: initialScope }).catch(() => {});
  return;
}

setPlayedToday(false);

      sessionStartedRef.current = false;
      spoiledRef.current = false;

      const dayKey = _todayKey();

      const startPromise = (async () => {
        const authToken = getTriviaAuthToken(token);

        const resp = await authenticatedRequest(`${API_ORIGIN}/api/trivia/start`, {
          rawResponse: true,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          },
          credentials: 'include',
          diagSource: 'useTriviaGame.start',
          body: JSON.stringify({ userId: resolvedUid, dayKey }),
        });

        let j = {};
        try { j = await resp.json(); } catch { j = {}; }

        if (resp.status === 409 && j && j.error === 'user_mismatch') {
          logOperationalWarning('trivia.start', { status: resp.status, code: 'user_mismatch' });

          const banner = document.createElement('div');
          banner.textContent = i18n.t('trivia.errors.sessionExpired', { ns: 'home' });
          Object.assign(banner.style, {
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            background: 'linear-gradient(135deg, #4B0082, #DAA520)',
            color: 'white',
            textAlign: 'center',
            padding: '12px 0',
            fontWeight: 'bold',
            fontFamily: "'Poppins', sans-serif",
            zIndex: '9999',
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            animation: 'fadeBanner 0.3s ease-out',
          });

          document.body.appendChild(banner);

          setTimeout(() => {
            banner.style.transition = 'opacity 0.4s';
            banner.style.opacity = '0';
            setTimeout(() => banner.remove(), 500);
          }, 3500);

          // Use the same server request and local AuthContext invalidation as
          // manual logout; never leave a stale authenticated socket behind.
          await requestServerLogout();
          logoutLocal();
          broadcastAuthLogout();

          window.location.href = '/login';
          return { ok: false, mismatch: true };
        }

        if (j && j.ok && j.already) {
          return { ok: true, already: true };
        }

        if (resp.ok && j && j.ok) {
          return { ok: true, already: false };
        }

        const errMsg = (j && j.error) ? j.error : `HTTP ${resp.status}`;
        throw new Error(errMsg);
      })();

      const questionsPromise = getDailyTriviaQuestions({ count: 3, userId: resolvedUid });

      let startResult;
      let qs;

      try {
        const results = await Promise.allSettled([startPromise, questionsPromise]);
        startResult = results[0].status === 'fulfilled' ? results[0].value : null;
        qs = results[1].status === 'fulfilled' ? results[1].value : null;
      } catch {
        startResult = null;
        qs = null;
      }

      if (startResult && startResult.mismatch) {
        safeSetLoadingTrivia(false);
        return;
      }

      if (startResult && startResult.ok && startResult.already) {
        resetTriviaBaseState();
        setQuestions([]);
        markTriviaAsPlayedMode();
        safeSetLoadingTrivia(false);
        fetchLeaderboard(initialScope, null, { preferCacheFirst: true, viewScope: initialScope }).catch(() => {});
        return;
      }

      if (startResult && !startResult.ok) {
        logOperationalWarning('trivia.start', { code: 'request_failed' });
        safeSetLoadingTrivia(false);
        try { alert(i18n.t('trivia.errors.startFailed', { ns: 'home' })); } catch {}
        return;
      }


      const safeQs = Array.isArray(qs) ? qs : [];

if (safeQs.length === 0) {
  resetTriviaBaseState();
  setQuestions([]);
  markTriviaAsPlayedMode();
  setPlayedToday(true);
  setFinished(true);
  setTriviaMode('played');
  sessionStartedRef.current = false;
  spoiledRef.current = false;
  safeSetLoadingTrivia(false);
  fetchLeaderboard(initialScope, null, { preferCacheFirst: true, viewScope: initialScope }).catch(() => {});
  return;
}

setTriviaMode('play');
startTriviaPlayState(safeQs);

sessionStartedRef.current = true;

safeSetLoadingTrivia(false);
    } finally {
      setTimeout(() => {
        triviaOpenInflightRef.current = false;
      }, 250);
    }
  }, [
  checkAlreadyPlayedToday,
  clockOffsetMs,
  fetchLeaderboard,
  fetchUserStats,
  getTriviaAuthToken,
  i18n,
  markTriviaAsPlayedMode,
  resolvedUid,
  sessionStartedRef,
  setFinished,
  setLbScope,
  safeSetLoadingTrivia,
  setAnswered,
  setPickedIdx,
  setPlayedToday,
  setQIndex,
  setQuestions,
  setQuestionStartedAt,
  setTicking,
  setTimeLeft,
  safeSetShowTrivia,
  setTriviaMode,
  setTriviaStartedAt,
  resetTriviaBaseState,
  spoiledRef,
  startTriviaPlayState,
  syncServerTime,
  token,
  triviaOpenInflightRef,
]);

  const nextQuestion = useCallback((delta = 0) => {
    if (qIndex + 1 < total) {
      setQIndex(qIndex + 1);
      setAnswered(false);
      setPickedIdx(-1);
      setTimeLeft(TIME_PER_QUESTION_MS);
      setQuestionStartedAt(Date.now());
      setTicking(true);
      return;
    }

    // Fin del set: enter one explicit finalizing state before persistence.
    // Rendering a finished question while the async submit is still pending
    // previously exposed a second, transient result screen.
    if (finalizationInFlightRef.current) return;
    finalizationInFlightRef.current = true;
    setFinished(true);
    setTicking(false);
    setTriviaMode('finalizing');
    const dayKey = _todayKey();

    // Usamos delta para evitar leer un "score" stale antes de que React
    // haya aplicado el último setScore (por ejemplo, si la última fue correcta).
    const finalScore = Number(score || 0) + Number(delta || 0);

    const persist = persistTriviaResultRef && persistTriviaResultRef.current;
    if (typeof persist === 'function') {
      Promise.resolve(persist(dayKey, finalScore, total))
        .then((resultWasSaved) => {
          // The save did not complete, so retain the one finished result view
          // instead of leaving the modal in its finalizing state.
          if (!resultWasSaved) setTriviaMode('play');
        })
        .finally(() => {
          finalizationInFlightRef.current = false;
        });
    } else {
      finalizationInFlightRef.current = false;
      setTriviaMode('play');
    }
  }, [
    qIndex,
    total,
    score,
    persistTriviaResultRef,
    setAnswered,
    setFinished,
    setPickedIdx,
    setQIndex,
    setQuestionStartedAt,
    setTicking,
    setTriviaMode,
    setTimeLeft,
  ]);

  const handlePickAnswer = useCallback((idx) => {
    if (answered || !current || timeLeft <= 0 || triviaMode !== 'play') return;

    setPickedIdx(idx);

    const picked = current.answers[idx];
    const isCorrect = isTriviaAnswerCorrect(current, picked, idx);

    if (isCorrect) {
      setScore((s) => s + 1);
    }

    setAnswered(true);
    setTicking(false);

    spoiledRef.current = true;

    if (!playedToday) {
      const dk = _todayKey();
      _setPlayedToday(resolvedUid, dk);
    }

    try {
      if (triviaAdvanceTimeoutRef.current) {
        clearTimeout(triviaAdvanceTimeoutRef.current);
      }
    } catch {}

    triviaAdvanceTimeoutRef.current = setTimeout(() => {
      triviaAdvanceTimeoutRef.current = null;
      nextQuestion(isCorrect ? 1 : 0);
    }, 800);
  }, [
    answered,
    current,
    timeLeft,
    triviaMode,
    playedToday,
    resolvedUid,
    nextQuestion,
    setAnswered,
    setPickedIdx,
    setScore,
    setTicking,
    spoiledRef,
    triviaAdvanceTimeoutRef,
      ]);

  // Cuenta regresiva por pregunta
  useEffect(() => {
    // Extra guard: jamás contar si no estamos en modo juego
    if (triviaMode !== 'play') return;
    if (!showTrivia) return;
    if (!ticking || answered || !current) return;
    if (!questionStartedAt) return;

    let rafId = null;
    let stop = false;

    const tick = () => {
      if (stop) return;

      const elapsed = Date.now() - questionStartedAt;
      const rem = Math.max(0, TIME_PER_QUESTION_MS - elapsed);
      setTimeLeft(rem);

      if (rem <= 0) {
        // tiempo agotado → marcar como no respondida/incorrecta y avanzar
        setTicking(false);
        if (!answered) {
          setAnswered(true);
          setPickedIdx(-1);

          // dar un pequeño delay para que el usuario vea el estado y luego avanzar
          try {
            if (triviaAdvanceTimeoutRef.current) clearTimeout(triviaAdvanceTimeoutRef.current);
          } catch {}

          triviaAdvanceTimeoutRef.current = setTimeout(() => {
            // Bloqueo local: ya se reveló la correcta por timeout
            spoiledRef.current = true;
            if (!playedToday) {
              const dk = _todayKey();
              _setPlayedToday(resolvedUid, dk);
            }

            if (!stop) {
              nextQuestion(0);
            }

            triviaAdvanceTimeoutRef.current = null;
          }, 800);
        }
        return;
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      stop = true;
      try { if (rafId) cancelAnimationFrame(rafId); } catch {}
      try { if (triviaAdvanceTimeoutRef.current) clearTimeout(triviaAdvanceTimeoutRef.current); } catch {}
      triviaAdvanceTimeoutRef.current = null;
    };
  }, [
    showTrivia,
    ticking,
    answered,
    current,
    questionStartedAt,
    triviaMode,
    playedToday,
    resolvedUid,
    nextQuestion,
    setAnswered,
    setPickedIdx,
    setTicking,
    setTimeLeft,
    spoiledRef,
    triviaAdvanceTimeoutRef,
  ]);

  const handleBeforeUnload = useCallback(() => {
    // En desarrollo (localhost) NO mandamos auto-submit para no generar partidas "ocultas" spam.
    if (process.env.NODE_ENV !== 'production') {
      return;
    }

    if (triviaMode === 'play' && sessionStartedRef.current) {
      try {
        const dayKey = _todayKey();
        const partialScore = Number(score || 0);
        const tot = Number(total || 0);

        if (partialScore <= 0) {
          _setPlayedToday(resolvedUid, dayKey);
          return;
        }

        const authToken = getTriviaAuthToken(token);
        _setPlayedToday(resolvedUid, dayKey);

        // keepalive preserves the authenticated request during page unload while
        // carrying the same Authorization header as normal submits.
        try {
          const payload = {
            userId: resolvedUid,
            score: partialScore,
            total: tot,
            startedAt: questionStartedAt || Date.now(),
            finishedAt: Date.now(),
          };
          fetch(`${API_ORIGIN}/api/trivia/submit`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            },
            body: JSON.stringify(payload),
            keepalive: true,
            credentials: 'include',
          }).catch(() => {});
        } catch {}
      } catch {}
    }
  }, [
    getTriviaAuthToken,
    questionStartedAt,
    resolvedUid,
    score,
    sessionStartedRef,
    token,
    total,
    triviaMode,
  ]);

  const handleVisibilityHidden = useCallback(() => {
    // En desarrollo (localhost) NO mandamos auto-submit al ir a background para no generar partidas "ocultas" spam.
    if (process.env.NODE_ENV !== 'production') {
      return;
    }

    if (triviaMode === 'play' && sessionStartedRef.current) {
      try {
        const dayKey = _todayKey();
        const partialScore = Number(score || 0);
        const tot = Number(total || 0);

        if (partialScore <= 0) {
          _setPlayedToday(resolvedUid, dayKey);
          return;
        }

        const authToken = getTriviaAuthToken(token);
        _setPlayedToday(resolvedUid, dayKey);

        const payload = {
          userId: resolvedUid,
          score: partialScore,
          total: tot,
          startedAt: questionStartedAt || Date.now(),
          finishedAt: Date.now(),
        };
        fetch(`${API_ORIGIN}/api/trivia/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify(payload),
          keepalive: true,
          credentials: 'include',
        }).catch(() => {});
      } catch {}
    }
  }, [
    getTriviaAuthToken,
    questionStartedAt,
    resolvedUid,
    score,
    sessionStartedRef,
    token,
    total,
    triviaMode,
  ]);

  useEffect(() => {
    if (!showTrivia) return;
    if (triviaOpenInflightRef.current) return;

    (async () => {
      if (triviaMode === 'play') {
  setFinished(false);
}

      const serverNow = await syncServerTime();
      setTriviaStartedAt((prev) => prev || (serverNow ?? (Date.now() + clockOffsetMs)));

      safeSetLoadingTrivia(true);
      try {
        if (triviaMode === 'played' || triviaMode === 'leaderboard') {
          safeSetLoadingTrivia(false);
          return;
        } else if (triviaMode === 'play' && !playedToday && sessionStartedRef.current) {
          if (questions && questions.length > 0) {
            startTriviaPlayState();
          } else {
            const qs = await getDailyTriviaQuestions({ count: 3, userId: resolvedUid });

            const safe = [];
            const seenText = new Set();

            for (const q of Array.isArray(qs) ? qs : []) {
              if (!q || !q.text) continue;
              const key = q.text.trim().toLowerCase();
              if (seenText.has(key)) continue;
              seenText.add(key);
              safe.push(q);
            }

            startTriviaPlayState(safe);
          }
        }
      } catch {}
      safeSetLoadingTrivia(false);
    })();
  }, [
    showTrivia,
    triviaOpenInflightRef,
    sessionStartedRef,
    triviaMode,
    playedToday,
    resolvedUid,
    questions,
    clockOffsetMs,
    syncServerTime,
    setFinished,
    setTriviaStartedAt,
    safeSetLoadingTrivia,
    resetTriviaBaseState,
    startTriviaPlayState,
  ]);

  const lastDayRef = useRef(_todayKey());

  useEffect(() => {
    function checkDayRollover() {
      const dayNow = _todayKey();
      if (lastDayRef.current === dayNow) {
        return;
      }

      lastDayRef.current = dayNow;
      _clearPlayedToday(resolvedUid);

      try {
        const cacheRaw = localStorage.getItem(TRIVIA_CACHE_KEY);
        if (cacheRaw) {
          const cache = JSON.parse(cacheRaw);
          if (!cache || cache.day !== dayNow) {
            localStorage.removeItem(TRIVIA_CACHE_KEY);
          }
        }
      } catch {}

      setPlayedToday(false);
      setTriviaMode('idle');
    }

    checkDayRollover();
    const id = setInterval(checkDayRollover, 60 * 1000);
    return () => clearInterval(id);
  }, [resolvedUid, setPlayedToday, setTriviaMode]);

  return {
    checkAlreadyPlayedToday,
    buildTriviaSubmitBody,
    normalizeTriviaSubmitInput,
    getTriviaAuthToken,
    submitTriviaResultToApi,
    buildOptimisticTriviaResult,
    persistTriviaResult,
    resetTriviaAndClose,
    openTriviaModal,
    handleBeforeUnload,
    handleVisibilityHidden,
    handlePickAnswer,
    nextQuestion,
  };
};

export default useTriviaGame;
